import { useId, useState } from "react";
import { useI18n } from "../lib/i18n";
import { fmtDateTime } from "../lib/time";

const COPY = {
  de: {
    attention: "Handlungsbedarf", tasks: "Care-Aufgaben", inventory: "Inventar",
    showAll: "Alle anzeigen", showLess: "Weniger anzeigen", open: "Öffnen",
    moreTopics: (n) => `${n} weitere${n === 1 ? "s Thema" : " Themen"} anzeigen`, lessTopics: "Weniger Themen anzeigen",
    allNotices: (n) => `${n} Hinweise insgesamt`, groupNotices: (n) => `${n} Hinweise anzeigen`,
    openSource: { injury_case: "Beschwerde öffnen", training_feedback: "Feedback öffnen", care_request: "Anfrage öffnen", service: "Service öffnen", inventory_item: "Inventar öffnen", today_item: "Maßnahme öffnen", daily_summary: "Tagesscore öffnen", meal_order: "Bestellung öffnen" },
    openDetails: "Details öffnen", openReconciliation: "Abgleich prüfen",
    openTask: "Aufgabe öffnen", allTasks: "Alle Aufgaben", openInventory: "Inventar öffnen",
    inventoryNotice: "Inventarhinweis", inventoryNotices: "Inventarhinweise",
    linkedTasks: "Diese Aufgaben sind im Handlungsbedarf verknüpft.",
    priority: { critical: "Kritisch", urgent: "Dringend", high: "Hoch", medium: "Mittel", normal: "Normal", low: "Niedrig" },
  },
  en: {
    attention: "Needs attention", tasks: "Care tasks", inventory: "Inventory",
    showAll: "Show all", showLess: "Show less", open: "Open",
    moreTopics: (n) => `Show ${n} more ${n === 1 ? "topic" : "topics"}`, lessTopics: "Show fewer topics",
    allNotices: (n) => `${n} notices in total`, groupNotices: (n) => `Show ${n} notices`,
    openSource: { injury_case: "Open complaint", training_feedback: "Open feedback", care_request: "Open request", service: "Open service", inventory_item: "Open inventory", today_item: "Open activity", daily_summary: "Open daily score", meal_order: "Open order" },
    openDetails: "Open details", openReconciliation: "Review reconciliation",
    openTask: "Open task", allTasks: "All tasks", openInventory: "Open inventory",
    inventoryNotice: "Inventory notice", inventoryNotices: "Inventory notices",
    linkedTasks: "These tasks are linked in Needs attention.",
    priority: { critical: "Critical", urgent: "Urgent", high: "High", medium: "Medium", normal: "Normal", low: "Low" },
  },
};

function useCopy() {
  const { lang, t } = useI18n();
  return { copy: COPY[lang === "en" ? "en" : "de"], lang, t };
}

// Preserve the canonical input order; only exact presentation groups are folded.
function groupRows(rows, keyFor) {
  const groups = [];
  const byKey = new Map();
  rows.forEach((row, index) => {
    const groupKey = keyFor(row);
    if (groupKey !== null && byKey.has(groupKey)) {
      byKey.get(groupKey).items.push(row);
      return;
    }
    const group = { key: groupKey ?? `single:${row.id ?? index}`, items: [row] };
    groups.push(group);
    if (groupKey !== null) byKey.set(groupKey, group);
  });
  return groups;
}

function isInventoryAttention(attention) {
  // The canonical reconciliation failure targets the schedule item, not an inventory item.
  return attention.entity_type === "inventory_item" || attention.kind === "inventory_reconciliation_failed";
}

function stableMetadata(value) {
  if (Array.isArray(value)) return value.map(stableMetadata);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, stableMetadata(value[key])]));
  }
  return value;
}

function attentionGroupKey(attention) {
  // Keep every supplied structured distinction, including reason/error codes. Current
  // reconciliation failures carry empty metadata; never infer a reason from their prose.
  return isInventoryAttention(attention) && attention.kind && attention.priority
    ? JSON.stringify([attention.kind, attention.priority, stableMetadata(attention.metadata || {})]) : null;
}

function forecastGroupKey(warning) {
  const fields = [warning.warning_type, warning.type, warning.reason];
  return fields.some((value) => value !== undefined && value !== null && value !== "")
    ? JSON.stringify([...fields, warning.priority ?? null]) : null;
}

function Priority({ value, copy }) {
  return copy.priority[value] ? <span className="care-cockpit-attention__priority" data-priority={value}>
    {copy.priority[value]}
  </span> : null;
}

function AttentionEntry({ attention, timezone, onOpenAttention, onOpenTask, compact = false }) {
  const { copy, lang } = useCopy();
  const date = attention.metadata?.target_at || attention.due_at;
  const actionLabel = attention.kind === "inventory_reconciliation_failed"
    ? copy.openReconciliation : copy.openSource[attention.entity_type] || copy.openDetails;
  return <article className="care-cockpit-attention__item" data-attention-id={attention.id} data-priority={attention.priority}>
    <div className="care-cockpit-attention__content">
      <p className="care-cockpit-attention__title">{attention.title}</p>
      {!compact && attention.body && <p className="care-cockpit-attention__body">{attention.body}</p>}
      <div className="care-cockpit-attention__meta">
        <Priority value={attention.priority} copy={copy} />
        {date && <time dateTime={date}>{fmtDateTime(date, timezone, lang)}</time>}
      </div>
    </div>
    <div className="care-cockpit-attention__actions">
      {onOpenAttention && <button type="button" className="pc-btn pc-btn-ghost care-cockpit-attention__open"
        aria-label={`${actionLabel}: ${attention.title}`} onClick={() => onOpenAttention(attention)}>{actionLabel}</button>}
      {attention.related_task && onOpenTask && <button type="button" className="pc-btn pc-btn-ghost care-cockpit-attention__task"
        aria-label={`${copy.openTask}: ${attention.related_task.title || attention.title}`}
        onClick={() => onOpenTask(attention.related_task)}>{copy.openTask}</button>}
    </div>
  </article>;
}

function AttentionGroup({ group, timezone, onOpenAttention, onOpenTask, onInventory, compact = false }) {
  const { copy } = useCopy();
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  if (group.items.length === 1) return <AttentionEntry attention={group.items[0]} timezone={timezone}
    onOpenAttention={onOpenAttention} onOpenTask={onOpenTask} compact={compact} />;
  const first = group.items[0];
  return <div className="care-cockpit-attention__group care-cockpit-inventory__group" data-priority={first.priority}>
    <div className="care-cockpit-attention__group-summary">
      <div className="care-cockpit-attention__content">
        <p className="care-cockpit-attention__title">{group.items.length} {copy.inventoryNotices}</p>
        <p className="care-cockpit-attention__body">{first.title}</p>
        <Priority value={first.priority} copy={copy} />
      </div>
      <div className="care-cockpit-attention__actions">
        <button type="button" className="pc-btn pc-btn-ghost" aria-expanded={expanded} aria-controls={listId}
          onClick={() => setExpanded(!expanded)}>{expanded ? copy.showLess : copy.groupNotices(group.items.length)}</button>
        {onInventory && <button type="button" className="pc-btn pc-btn-ghost" onClick={onInventory}>{copy.openInventory}</button>}
      </div>
    </div>
    <div id={listId} hidden={!expanded} className="care-cockpit-attention__group-items">
      {expanded && group.items.map((attention) => <AttentionEntry key={attention.id} attention={attention}
        timezone={timezone} onOpenAttention={onOpenAttention} onOpenTask={onOpenTask} compact />)}
    </div>
  </div>;
}

export default function CareCockpitAttention({ attentions = [], timezone, onOpenAttention, onOpenTask, onInventory }) {
  const { copy } = useCopy();
  const [expanded, setExpanded] = useState(false);
  const headingId = useId();
  const listId = useId();
  const groups = groupRows(attentions, attentionGroupKey);
  if (!groups.length) return null;
  return <section className="care-cockpit-attention" data-testid="player-record-attention" aria-labelledby={headingId}>
    <header className="care-cockpit-attention__header"><h2 id={headingId}>{copy.attention}</h2>
      {groups.length > 2 && <button type="button" className="pc-btn pc-btn-ghost care-cockpit-attention__more"
        aria-expanded={expanded} aria-controls={listId}
        aria-label={`${expanded ? copy.lessTopics : copy.moreTopics(groups.length - 2)} · ${copy.allNotices(attentions.length)}`}
        onClick={() => setExpanded(!expanded)}>
        {expanded ? copy.lessTopics : copy.moreTopics(groups.length - 2)}
      </button>}
    </header>
    <div id={listId} className="care-cockpit-attention__list">
      {(expanded ? groups : groups.slice(0, 2)).map((group) => <AttentionGroup key={group.key} group={group}
        timezone={timezone} onOpenAttention={onOpenAttention} onOpenTask={onOpenTask} onInventory={onInventory} />)}
    </div>
  </section>;
}

export function CareCockpitTasks({ tasks = [], attentions = [], timezone, onOpenTask, onAllTasks }) {
  const { copy, lang } = useCopy();
  const headingId = useId();
  const attentionIds = new Set(attentions.map((attention) => attention.id).filter(Boolean));
  const linkedTaskIds = new Set(attentions.map((attention) => attention.related_task?.id).filter(Boolean));
  const additionalTasks = tasks.filter((task) => !linkedTaskIds.has(task.id)
    && !(task.source_type === "attention" && attentionIds.has(task.source_id)));
  if (!tasks.length) return null;
  return <section className="care-cockpit-tasks" data-testid="player-record-care-tasks" aria-labelledby={headingId}>
    <header className="care-cockpit-tasks__header">
      <h2 id={headingId}>{copy.tasks}</h2>
      {onAllTasks && <button type="button" className="pc-btn pc-btn-ghost" onClick={onAllTasks}>{copy.allTasks}</button>}
    </header>
    <div className="care-cockpit-tasks__list">
      {additionalTasks.slice(0, 3).map((task) => <article className="care-cockpit-tasks__item" key={task.id} data-task-id={task.id}>
        <div className="care-cockpit-tasks__content">
          <p className="care-cockpit-tasks__title">{task.title}</p>
          <div className="care-cockpit-tasks__meta">
            <Priority value={task.priority} copy={copy} />
            {task.due_at && <time dateTime={task.due_at}>{fmtDateTime(task.due_at, timezone, lang)}</time>}
          </div>
        </div>
        {onOpenTask && <button type="button" className="pc-btn pc-btn-ghost" aria-label={`${copy.openTask}: ${task.title}`}
          onClick={() => onOpenTask(task)}>{copy.openTask}</button>}
      </article>)}
    </div>
    {!additionalTasks.length && <p className="care-cockpit-tasks__empty">{copy.linkedTasks}</p>}
  </section>;
}

export function CareCockpitInventory({ attentions = [], warnings = [], timezone, onOpenAttention, onOpenTask, onInventory }) {
  const { copy, lang, t } = useCopy();
  const headingId = useId();
  const inventoryAttentions = attentions.filter(isInventoryAttention);
  // Status already supplies warning forecasts; respect explicit non-warning rows if supplied.
  const warningForecasts = warnings.filter((warning) => warning.attention_required !== false);
  const forecastText = (warning) => {
    if (warning.message || warning.reason) return warning.message || warning.reason;
    const unitKey = `unit_${warning.unit}`;
    const unit = warning.unit ? t(unitKey) : "";
    return `${warning.quantity ?? "–"} ${unit === unitKey ? warning.unit : unit}`.trim();
  };
  // Forecasts remain separate from attentions: an item ID alone does not identify the same issue.
  const groups = [
    ...groupRows(inventoryAttentions, attentionGroupKey).map((group) => ({ ...group, source: "attention" })),
    ...groupRows(warningForecasts, forecastGroupKey).map((group) => ({ ...group, source: "forecast" })),
  ];
  if (!groups.length) return null;
  return <section className="care-cockpit-inventory" data-testid="player-record-inventory-warnings" aria-labelledby={headingId}>
    <header className="care-cockpit-inventory__header"><h2 id={headingId}>{copy.inventory}</h2></header>
    <div className="care-cockpit-inventory__list">
      {groups.slice(0, 2).map((group) => group.source === "attention"
        ? <AttentionGroup key={`attention:${group.key}`} group={group} timezone={timezone}
          onOpenAttention={onOpenAttention} onOpenTask={onOpenTask} compact />
        : <article key={`forecast:${group.key}`} className="care-cockpit-inventory__forecast" data-count={group.items.length}>
          <p className="care-cockpit-inventory__title">{group.items.length > 1
            ? `${group.items.length} ${copy.inventoryNotices}` : group.items[0].name || copy.inventoryNotice}</p>
          <p className="care-cockpit-inventory__body">{forecastText(group.items[0])}</p>
          {group.items[0].expected_depletion_at && <p className="care-cockpit-inventory__body">
            {t("expectedDepletion")}: <time dateTime={group.items[0].expected_depletion_at}>
              {fmtDateTime(group.items[0].expected_depletion_at, timezone, lang)}
            </time>
          </p>}
        </article>)}
    </div>
    {onInventory && <button type="button" className="pc-btn pc-btn-ghost care-cockpit-inventory__all" onClick={onInventory}>
      {copy.openInventory}
    </button>}
  </section>;
}
